/**
 * FLUX Studio — API Client
 *
 * All API calls to /api/studio/* go through here.
 * Handles JWT storage, refresh, and 401 interception.
 */

const TOKEN_KEY = 'flux_studio_jwt';

let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(cb: () => void) {
  onUnauthorized = cb;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};

  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const opts: RequestInit = { method, headers };
  if (body) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(path, opts);

  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'Sessão expirada');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }

  if (res.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return res.json() as Promise<T>;
  }
  return res.text() as unknown as T;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Auth ──

export async function authWithGoogle(idToken: string) {
  return request<{ token: string; name: string; email: string }>(
    'POST',
    '/api/studio/auth',
    { idToken },
  );
}

export async function exchangeCode(code: string) {
  return request<{ token: string; name: string; email: string }>(
    'POST',
    '/api/studio/exchange',
    { code },
  );
}

// ── Tree ──

export interface TreeNode {
  key: number;
  name: string;
  type: 'classe' | 'file';
  children?: TreeNode[];
  hasSkills?: boolean;
  editable?: boolean;
  /** Arquivo de apoio (`~`): caminho na especificação, ex. `skill/references/x.md` (ADR-070) */
  displayName?: string;
}

export async function fetchTree() {
  return request<TreeNode[]>('GET', '/api/studio/tree');
}

// ── Files ──

export interface FileData {
  ikey: number;
  name: string;
  content: string;
  path: string[];
  editable: boolean;
  readOnlyReason?: string;
}

export async function fetchFile(ivfsKey: number) {
  return request<FileData>('GET', `/api/studio/file/${ivfsKey}`);
}

export async function saveFile(ivfsKey: number, content: string) {
  return request<{ version: number; renamed?: string }>('PUT', `/api/studio/file/${ivfsKey}`, {
    content,
  });
}

export async function createFile(parentKey: number, name: string, content: string) {
  return request<{ ikey: number }>('POST', '/api/studio/file', {
    parentKey,
    name,
    content,
  });
}

export async function deleteFile(ivfsKey: number) {
  return request<void>('DELETE', `/api/studio/file/${ivfsKey}`);
}

export async function renameFile(ivfsKey: number, name: string) {
  return request<{ name: string; content: string }>('PATCH', `/api/studio/file/${ivfsKey}/rename`, { name });
}

export async function searchContent(query: string) {
  return request<{ keys: number[] }>('GET', `/api/studio/search?q=${encodeURIComponent(query)}`);
}

export async function moveFile(ivfsKey: number, targetParentKey: number) {
  return request<{ moved: boolean }>('PATCH', `/api/studio/file/${ivfsKey}/move`, { targetParentKey });
}

export interface HistoryEntry {
  date: string;
  time: string;
  user: string;
  diff: {
    added: string[];
    removed: string[];
  };
}

export async function fetchHistory(ivfsKey: number) {
  return request<{ entries: HistoryEntry[] }>('GET', `/api/studio/file/${ivfsKey}/history`);
}

// ── Skill Builder ──

export interface SkillBuilderResponse {
  markdown: string;
  explanation: string;
  changes?: Array<{
    before: string;
    after: string;
  }>;
  snapshot?: string;
}

/** Send multipart/form-data request with JWT auth */
async function requestMultipart<T>(path: string, formData: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Don't set Content-Type — browser sets it with boundary for FormData

  const res = await fetch(path, { method: 'POST', headers, body: formData });

  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'Sessão expirada');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return res.json() as Promise<T>;
  }
  return res.text() as unknown as T;
}

export async function chatSkillBuilder(
  prompt: string,
  currentContent?: string,
  skillPath?: string[],
  action?: string,
  history?: Array<{ role: string; text: string }>,
  otherSkillKeys?: number[],
  files?: File[],
) {
  // Use multipart when files are attached
  if (files && files.length > 0) {
    const formData = new FormData();
    formData.append('prompt', prompt);
    if (currentContent) formData.append('currentContent', currentContent);
    if (skillPath) formData.append('skillPath', JSON.stringify(skillPath));
    if (action) formData.append('action', action);
    if (history) formData.append('history', JSON.stringify(history));
    if (otherSkillKeys) formData.append('otherSkillKeys', JSON.stringify(otherSkillKeys));
    for (const file of files) {
      formData.append('files', file, file.name);
    }
    return requestMultipart<SkillBuilderResponse>('/api/studio/skill-builder', formData);
  }

  // JSON body (no files)
  return request<SkillBuilderResponse>('POST', '/api/studio/skill-builder', {
    prompt,
    currentContent,
    skillPath,
    action,
    history,
    otherSkillKeys,
  });
}

// ── Ops Dashboard ──

export async function fetchLiveStatus() {
  return request<{
    workers: { active: number; pending: number };
    pipeline: {
      active: Array<Record<string, unknown>>;
      recent: Array<Record<string, unknown>>;
    };
    timestamp: number;
  }>('GET', '/api/ops/live-status');
}

export async function fetchPoolStats() {
  return request<{
    providers: Array<Record<string, unknown>>;
    timestamp: number;
  }>('GET', '/api/ops/pool-stats');
}

export async function fetchOpsMessages(params: {
  limit?: number; offset?: number; status?: string; q?: string; days?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.days) qs.set('days', String(params.days));
  return request<{
    total: number;
    messages: Array<Record<string, unknown>>;
    limit: number; offset: number;
  }>('GET', `/api/ops/messages?${qs.toString()}`);
}

export async function fetchOpsMessage(id: string) {
  return request<{
    message: Record<string, unknown>;
    entregas: Array<Record<string, unknown>>;
    demandas: Array<Record<string, unknown>>;
    evolucoes: Array<Record<string, unknown>>;
    toques: Array<Record<string, unknown>>;
  }>('GET', `/api/ops/message/${id}`);
}

export async function fetchOpsThread(messageId: string) {
  return request<{
    thread: Array<Record<string, unknown>>;
    rootMessageId: string;
  }>('GET', `/api/ops/thread/${encodeURIComponent(messageId)}`);
}

export async function fetchOpsDemands(params: {
  limit?: number; offset?: number; status?: string; days?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.status) qs.set('status', params.status);
  if (params.days) qs.set('days', String(params.days));
  return request<{
    demands: Array<Record<string, unknown>>;
    limit: number; offset: number;
  }>('GET', `/api/ops/demands?${qs.toString()}`);
}

export async function fetchOpsPerformance(days = 30) {
  return request<{
    daily: Array<Record<string, unknown>>;
    today: Record<string, unknown>;
    demandsToday: number;
    models: Array<Record<string, unknown>>;
    timestamp: number;
  }>('GET', `/api/ops/performance?days=${days}`);
}

export async function fetchOpsAttachments(q: string) {
  return request<{
    found: boolean;
    msgId: string;
    attachments: Array<{
      MENSAGEM_ID: string;
      ID_EXTERNO: string;
      NOME_ARQUIVO: string;
      HASH_SHA256: string;
      INTERPRETACAO_LLM: string | null;
      MODELO_UTILIZADO: string | null;
      DATA_INTERPRETACAO: string | null;
    }>;
    message?: string;
  }>('GET', `/api/ops/attachments?q=${encodeURIComponent(q)}`);
}
